import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import {
    Button,
    Checkbox,
    Divider,
    Dropdown,
    Form,
    FormGroup,
    Input,
    InputPlaceholder,
    Message,
    Modal,
    ModalActions,
    ModalContent,
    ModalHeader,
    Select,
    TabNav,
    TabNavItem,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Toggle,
} from '@/platform/ui';
import { Deferred, useForm, usePage } from '@inertiajs/react';
import { useState } from 'react';

const TABS = ['Gegevens', 'Entiteiten', 'Meldingen', 'Diensten'];

const TYPE_OPTIONS = [
    { id: 'internal', name: 'Intern' },
    { id: 'external', name: 'Extern' },
];

const TWO_FACTOR_OPTIONS = [
    { id: 'email', name: 'Code per e-mail' },
    { id: 'google', name: '(Google) Authenticator (OTP)' },
];

const EMAIL_SETTINGS = [
    { key: 'xml', label: 'Aanleveren XML' },
    { key: 'wtl', label: 'Aanleveren Wtl beschikking' },
    {
        key: 'whk',
        label: 'Aanleveren beschikkingen gedifferentieerde premie Whk',
    },
    { key: 'missing-info', label: 'Aanvullen ontbrekende contactgegevens' },
    { key: 'assistance', label: 'Assistentie' },
    { key: 'check-entrants', label: 'Aanvullen instroom' },
    { key: 'assessment', label: 'Inventarisatie controleren' },
    { key: 'education-documents', label: 'Aanleveren documenten scholing' },
    { key: 'dgv-updates', label: 'Melding voor ontvangen doelgroepverklaring' },
    {
        key: 'dgv-rejections',
        label: 'Melding voor afwijzing doelgroepverklaring',
    },
    { key: 'lkv-deletions', label: 'Melding voor verwijdering gegevens LKV' },
];

const asObject = value => (Array.isArray(value) ? {} : (value ?? {}));

const ContactModal = ({ contact, onClose }) => {
    const { auth, roles, services } = usePage().props;
    const [tab, setTab] = useState(TABS[0]);

    const can = permission => auth.permissions?.includes(permission);
    const canEditTwoFactor = can('update users allowed two factor types');
    const canEditSsoOnly = can('set user to sso only');
    const isOwnAccount = contact?.user?.id === auth.user.id;

    const entities = [...(auth.customer.entities ?? [])].sort(
        (a, b) => a.number - b.number
    );

    const { data, setData, post, patch, transform, errors, processing } =
        useForm({
            type: TYPE_OPTIONS.some(option => option.id === contact?.type)
                ? contact.type
                : 'internal',
            company: contact?.company ?? '',
            first_name: contact?.first_name ?? '',
            affix: contact?.affix ?? '',
            last_name: contact?.last_name ?? '',
            email: contact?.email ?? '',
            telephone_number: contact?.telephone_number ?? '',
            mobile_number: contact?.mobile_number ?? '',
            department: contact?.department ?? '',
            function: contact?.function ?? '',
            is_primary: !!contact?.is_primary,
            entities: contact?.user?.entity_ids ?? [],
            email_settings: asObject(contact?.email_settings),
            service_settings: asObject(contact?.service_settings),
            user_details: {
                has_user_account: !!contact?.user,
                user_role_ids: contact?.user?.roles?.map(role => role.id) ?? [],
                send_invite: false,
                ...(canEditTwoFactor && {
                    allowed_2fa: contact?.user?.allowed_2fa ?? ['google'],
                }),
                ...(canEditSsoOnly && {
                    sso_only: !!contact?.user?.sso_only,
                }),
            },
        });

    const setUserDetail = (key, value) =>
        setData('user_details', { ...data.user_details, [key]: value });

    const setAll = (key, items, checked) =>
        setData(
            key,
            Object.fromEntries(items.map(item => [item.key, checked]))
        );

    transform(payload =>
        payload.user_details.has_user_account
            ? payload
            : {
                  ...payload,
                  user_details: {
                      ...payload.user_details,
                      allowed_2fa: undefined,
                  },
              }
    );

    const handleSubmit = () => {
        const options = {
            preserveScroll: true,
            onSuccess: () => onClose(),
            onError: () => setTab(TABS[0]),
        };

        if (contact?.id) {
            patch(
                `/customers/${auth.customer.id}/contacts/${contact.id}`,
                options
            );

            return;
        }

        post(`/customers/${auth.customer.id}/contacts`, options);
    };

    const userDetails = data.user_details;
    const allEntitiesSelected =
        entities.length > 0 && data.entities.length === entities.length;
    const allEmailSettingsSelected = EMAIL_SETTINGS.every(
        setting => data.email_settings[setting.key]
    );
    const allServicesSelected =
        services?.length > 0 &&
        services.every(service => data.service_settings[service.key]);

    return (
        <Modal open={true} onClose={onClose} size='large'>
            <ModalHeader>
                {contact?.id
                    ? 'Contactpersoon wijzigen'
                    : 'Nieuwe contactpersoon toevoegen'}
            </ModalHeader>
            <ModalContent>
                <TabNav label='Onderdeel' className='mb-4'>
                    {TABS.map(name => (
                        <TabNavItem
                            key={name}
                            active={tab === name}
                            onClick={() => setTab(name)}
                        >
                            {name}
                        </TabNavItem>
                    ))}
                </TabNav>

                {tab === 'Gegevens' && (
                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <Select
                                label='Intern/Extern'
                                options={TYPE_OPTIONS}
                                value={data.type}
                                onChange={event =>
                                    setData('type', event.target.value)
                                }
                                error={errors.type}
                            />
                            {contact?.id && data.type === 'external' && (
                                <Message
                                    variant='warning'
                                    content='Na afronden van de vragen nemen wij na 3 dagen contact op met de externe partij. Zou je ons alvast willen introduceren?'
                                />
                            )}
                            {data.type === 'external' && (
                                <Input
                                    label='Bedrijfsnaam'
                                    required
                                    value={data.company}
                                    onChange={event =>
                                        setData('company', event.target.value)
                                    }
                                    error={errors.company}
                                />
                            )}
                            <div className='grid grid-cols-1 gap-4 sm:grid-cols-5'>
                                <div className='sm:col-span-2'>
                                    <Input
                                        label='Voornaam'
                                        required
                                        value={data.first_name}
                                        onChange={event =>
                                            setData(
                                                'first_name',
                                                event.target.value
                                            )
                                        }
                                        error={errors.first_name}
                                    />
                                </div>
                                <Input
                                    label='Tussenvoegsel'
                                    value={data.affix}
                                    onChange={event =>
                                        setData('affix', event.target.value)
                                    }
                                    error={errors.affix}
                                />
                                <div className='sm:col-span-2'>
                                    <Input
                                        label='Achternaam'
                                        required
                                        value={data.last_name}
                                        onChange={event =>
                                            setData(
                                                'last_name',
                                                event.target.value
                                            )
                                        }
                                        error={errors.last_name}
                                    />
                                </div>
                            </div>
                            <Input
                                label='E-mailadres'
                                type='email'
                                required
                                disabled={isOwnAccount}
                                value={data.email}
                                onChange={event =>
                                    setData('email', event.target.value)
                                }
                                error={errors.email}
                            />
                            <FormGroup equal>
                                <Input
                                    label='Telefoon'
                                    required
                                    value={data.telephone_number}
                                    onChange={event =>
                                        setData(
                                            'telephone_number',
                                            event.target.value
                                        )
                                    }
                                    error={errors.telephone_number}
                                />
                                <Input
                                    label='Mobiel'
                                    value={data.mobile_number}
                                    onChange={event =>
                                        setData(
                                            'mobile_number',
                                            event.target.value
                                        )
                                    }
                                    error={errors.mobile_number}
                                />
                            </FormGroup>
                            <FormGroup equal>
                                <Input
                                    label='Afdeling'
                                    value={data.department}
                                    onChange={event =>
                                        setData(
                                            'department',
                                            event.target.value
                                        )
                                    }
                                    error={errors.department}
                                />
                                <Input
                                    label='Functie'
                                    value={data.function}
                                    onChange={event =>
                                        setData('function', event.target.value)
                                    }
                                    error={errors.function}
                                />
                            </FormGroup>

                            <Checkbox
                                label='Hoofdcontactpersoon'
                                checked={data.is_primary}
                                onChange={checked =>
                                    setData('is_primary', checked)
                                }
                                error={errors.is_primary}
                            />

                            <Checkbox
                                label='Deze contactpersoon heeft een gebruikersaccount'
                                checked={userDetails.has_user_account}
                                onChange={checked =>
                                    setUserDetail('has_user_account', checked)
                                }
                                error={errors['user_details.has_user_account']}
                            />

                            {userDetails.has_user_account && (
                                <>
                                    <Deferred
                                        data={['roles']}
                                        fallback={
                                            <InputPlaceholder
                                                label='Rollen van de gebruiker'
                                                placeholder='Rollen worden geladen...'
                                            />
                                        }
                                    >
                                        <Dropdown
                                            label='Rollen van de gebruiker'
                                            required
                                            multiple
                                            options={roles ?? []}
                                            value={userDetails.user_role_ids}
                                            onChange={value =>
                                                setUserDetail(
                                                    'user_role_ids',
                                                    value
                                                )
                                            }
                                            error={
                                                errors[
                                                    'user_details.user_role_ids'
                                                ]
                                            }
                                        />
                                    </Deferred>
                                    {userDetails.user_role_ids.length > 1 && (
                                        <Message
                                            variant='warning'
                                            content={`Klant-accounts hebben meestal maar één rol. Weet je zeker dat je er ${userDetails.user_role_ids.length} wilt toewijzen?`}
                                        />
                                    )}

                                    {(canEditTwoFactor || canEditSsoOnly) && (
                                        <Divider content='Authenticatie' />
                                    )}
                                    {canEditTwoFactor && (
                                        <Dropdown
                                            label='Toegestane tweestapsverificatie type'
                                            multiple
                                            options={TWO_FACTOR_OPTIONS}
                                            value={userDetails.allowed_2fa}
                                            onChange={value =>
                                                setUserDetail(
                                                    'allowed_2fa',
                                                    value
                                                )
                                            }
                                            error={
                                                errors[
                                                    'user_details.allowed_2fa'
                                                ]
                                            }
                                        />
                                    )}
                                    {canEditSsoOnly && (
                                        <Toggle
                                            label='Alleen SSO'
                                            description='Indien ingeschakeld, kan de gebruiker alleen inloggen via SSO. Het wachtwoord zal niet meer werken voor authenticatie.'
                                            checked={userDetails.sso_only}
                                            onChange={checked =>
                                                setUserDetail(
                                                    'sso_only',
                                                    checked
                                                )
                                            }
                                            error={
                                                errors['user_details.sso_only']
                                            }
                                        />
                                    )}

                                    <Divider content='Wachtwoord' />
                                    <Checkbox
                                        label='Verstuur (opnieuw) een e-mail voor wachtwoordherstel naar de gebruiker'
                                        checked={userDetails.send_invite}
                                        onChange={checked =>
                                            setUserDetail(
                                                'send_invite',
                                                checked
                                            )
                                        }
                                    />
                                </>
                            )}
                        </FormGroup>
                    </Form>
                )}

                {tab === 'Entiteiten' && (
                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Entiteit</TableHeaderCell>
                                <TableHeaderCell textAlign='right'>
                                    <Toggle
                                        label='Toegang'
                                        checked={allEntitiesSelected}
                                        onChange={checked =>
                                            setData(
                                                'entities',
                                                checked
                                                    ? entities.map(
                                                          entity => entity.id
                                                      )
                                                    : []
                                            )
                                        }
                                    />
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {entities.length === 0 && (
                                <NoDataRow numberOfColumns={2} />
                            )}
                            {entities.map(entity => (
                                <TableRow key={entity.id}>
                                    <TableCell>
                                        {entity.number} - {entity.name}
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        <Toggle
                                            checked={data.entities.includes(
                                                entity.id
                                            )}
                                            onChange={checked =>
                                                setData(
                                                    'entities',
                                                    checked
                                                        ? [
                                                              ...data.entities,
                                                              entity.id,
                                                          ]
                                                        : data.entities.filter(
                                                              id =>
                                                                  id !==
                                                                  entity.id
                                                          )
                                                )
                                            }
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}

                {tab === 'Meldingen' && (
                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Melding</TableHeaderCell>
                                <TableHeaderCell textAlign='right'>
                                    <Toggle
                                        label='Alles'
                                        checked={allEmailSettingsSelected}
                                        onChange={checked =>
                                            setAll(
                                                'email_settings',
                                                EMAIL_SETTINGS,
                                                checked
                                            )
                                        }
                                    />
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {EMAIL_SETTINGS.map(setting => (
                                <TableRow key={setting.key}>
                                    <TableCell>{setting.label}</TableCell>
                                    <TableCell textAlign='right'>
                                        <Toggle
                                            checked={
                                                !!data.email_settings[
                                                    setting.key
                                                ]
                                            }
                                            onChange={checked =>
                                                setData('email_settings', {
                                                    ...data.email_settings,
                                                    [setting.key]: checked,
                                                })
                                            }
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}

                {tab === 'Diensten' && (
                    <Deferred
                        data={['services']}
                        fallback={
                            <InputPlaceholder
                                label='Diensten'
                                placeholder='Diensten worden geladen...'
                            />
                        }
                    >
                        <Table striped>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>Dienst</TableHeaderCell>
                                    <TableHeaderCell textAlign='right'>
                                        <Toggle
                                            label='Toegang'
                                            checked={allServicesSelected}
                                            onChange={checked =>
                                                setAll(
                                                    'service_settings',
                                                    services ?? [],
                                                    checked
                                                )
                                            }
                                        />
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {services?.length === 0 && (
                                    <NoDataRow numberOfColumns={2} />
                                )}
                                {services?.map(service => (
                                    <TableRow key={service.id}>
                                        <TableCell>{service.name}</TableCell>
                                        <TableCell textAlign='right'>
                                            <Toggle
                                                checked={
                                                    !!data.service_settings[
                                                        service.key
                                                    ]
                                                }
                                                onChange={checked =>
                                                    setData(
                                                        'service_settings',
                                                        {
                                                            ...data.service_settings,
                                                            [service.key]:
                                                                checked,
                                                        }
                                                    )
                                                }
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Deferred>
                )}
            </ModalContent>
            <ModalActions>
                <Button onClick={onClose}>Annuleren</Button>
                <Button
                    variant='primary'
                    onClick={handleSubmit}
                    loading={processing}
                >
                    Opslaan
                </Button>
            </ModalActions>
        </Modal>
    );
};

export default ContactModal;
